
import { useState, useEffect } from "react";
import { DiaryEntry } from "@/utils/diary/types";
import { getUserEntries } from "@/utils/diary/storage";
import { subMonths } from "date-fns";

export function useEntriesData(userId: string | undefined) {
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [timeRange, setTimeRange] = useState<string>("3months");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load entries when userId changes
  useEffect(() => {
    if (!userId) {
      setEntries([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    getUserEntries(userId)
      .then(userEntries => {
        if (Array.isArray(userEntries)) {
          // Sort entries by date
          userEntries.sort((a, b) => {
            return new Date(b.date || b.createdAt).getTime() - 
                  new Date(a.date || a.createdAt).getTime();
          });
          
          setEntries(userEntries);
        } else {
          console.error("Expected array from getUserEntries but got:", userEntries);
          setEntries([]);
        }
      })
      .catch(error => {
        console.error("Error loading entries:", error);
        setEntries([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userId]);

  const getFilteredEntries = () => {
    const now = new Date();
    
    switch (timeRange) {
      case "1month":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 1);
        });
      case "3months":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 3);
        });
      case "6months":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 6);
        });
      case "1year":
        return entries.filter(entry => {
          const entryDate = new Date(entry.date || entry.createdAt);
          return entryDate >= subMonths(now, 12);
        });
      case "all":
        return entries;
      default:
        return entries;
    }
  };

  return {
    entries,
    timeRange, 
    setTimeRange,
    getFilteredEntries
  };
}
